import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BOWER_LOGO = "https://customer-assets.emergentagent.com/job_9905ee30-c002-464f-b890-465ab6cc70dc/artifacts/9497g015_Bower%20School%20Logo-01.png";

// Stock images for event cards
const CARD_IMAGES = [
  "https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=600&h=400&fit=crop",
  "https://images.unsplash.com/photo-1505373877841-8d25f7d46678?w=600&h=400&fit=crop",
  "https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=600&h=400&fit=crop",
  "https://images.unsplash.com/photo-1591115765373-5207764f72e7?w=600&h=400&fit=crop",
  "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=600&h=400&fit=crop",
];

const EVENT_TYPE_COLORS = {
  Workshop: "bg-[#8e3d00] text-[#ffba97]",
  Seminar: "bg-[#1438b2] text-[#c5cdff]",
  Competition: "bg-[#8e3d00] text-[#ffba97]",
  Campus: "bg-[#1438b2] text-[#c5cdff]",
  Networking: "bg-[#3734f6] text-[#cbccff]",
};

const EventsListingPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [audienceFilter, setAudienceFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [modeFilter, setModeFilter] = useState("All");
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/events?status=active`);
      setEvents(res.data);
    } catch (err) {
      console.error("Failed to fetch events:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredEvents = events.filter((e) => {
    if (audienceFilter !== "All" && e.audience !== audienceFilter) return false;
    if (typeFilter !== "All" && e.event_type !== typeFilter) return false;
    if (modeFilter !== "All" && e.mode !== modeFilter) return false;
    return true;
  });

  const getCardImage = (event, index) => {
    if (event.card_image) return event.card_image;
    return CARD_IMAGES[index % CARD_IMAGES.length];
  };

  return (
    <div className="min-h-screen" style={{ background: "#0f0f32", color: "#e1dfff", fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="events-listing-page">
      {/* TopNavBar */}
      <nav className="fixed top-0 w-full z-50" style={{ background: "linear-gradient(to bottom, #0f0f32, transparent)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)" }}>
        <div className="flex justify-between items-center max-w-7xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center gap-8">
            <Link to="/" data-testid="events-logo-link">
              <img src={BOWER_LOGO} alt="Bower School" className="h-12 md:h-14 object-contain brightness-0 invert" />
            </Link>
            <div className="hidden md:flex items-center gap-8 font-bold tracking-tight text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              <Link to="/events" className="border-b-2 pb-1 transition-all duration-300" style={{ color: "#b9c3ff", borderColor: "#2F4DC5" }} data-testid="nav-events">Events</Link>
              <a className="hover:text-white transition-colors" style={{ color: "#c5c5d6" }} href="#">Programs</a>
              <a className="hover:text-white transition-colors" style={{ color: "#c5c5d6" }} href="#">About</a>
              <a className="hover:text-white transition-colors" style={{ color: "#c5c5d6" }} href="#">Admissions</a>
            </div>
          </div>
          <Link to="/" className="px-6 py-2.5 rounded-full font-bold text-sm transition-all shadow-lg" style={{ background: "linear-gradient(135deg, #b9c3ff, #2f4dc5)", color: "#001356" }} data-testid="nav-register-btn">
            Register Now
          </Link>
        </div>
      </nav>

      <main className="pt-24 min-h-screen">
        {/* Hero Section */}
        <section className="relative px-4 md:px-8 py-20 max-w-7xl mx-auto overflow-hidden">
          {/* Background glow */}
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full blur-[100px]" style={{ background: "rgba(47, 77, 197, 0.15)" }} />
          <div className="absolute bottom-0 right-10 w-64 h-64 rounded-full blur-[80px]" style={{ background: "rgba(255, 182, 143, 0.05)" }} />
          <div className="relative z-10 max-w-4xl">
            <h1 className="font-extrabold text-5xl md:text-7xl tracking-tighter mb-6 leading-[1.1]" style={{ color: "#e1dfff" }} data-testid="events-hero-title">
              Build. Learn. <br />
              <span className="italic" style={{ color: "#b9c3ff" }}>Experience</span> Entrepreneurship.
            </h1>
            <p className="text-xl md:text-2xl max-w-2xl mb-10 leading-relaxed font-light" style={{ color: "#c5c5d6" }}>
              Workshops, competitions, masterclasses, and real-world experiences designed for future founders.
            </p>
            <div className="flex flex-wrap gap-3">
              {["Student Events", "UG Admissions", "Founder & Professional", "Campus Experiences"].map((tag) => (
                <span key={tag} className="px-4 py-1.5 rounded-full text-sm font-medium" style={{ background: "#26264a", border: "1px solid rgba(68,70,84,0.15)", color: tag === "Student Events" ? "#ffb68f" : "#c5c5d6" }}>
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* Content Grid */}
        <section className="max-w-7xl mx-auto px-4 md:px-8 pb-32">
          <div className="flex gap-10">
            {/* Sidebar Filters */}
            <aside className="hidden lg:flex flex-col w-64 shrink-0 h-fit sticky top-24 rounded-xl p-6 shadow-2xl text-sm font-medium" style={{ background: "#17173b" }} data-testid="events-filters">
              <div className="mb-8">
                <h3 className="text-lg font-bold text-white mb-1">Filters</h3>
                <p className="text-xs opacity-70" style={{ color: "#c5c5d6" }}>Find your path</p>
              </div>
              <div className="space-y-8">
                {/* Audience */}
                <div>
                  <span className="text-[10px] uppercase tracking-widest font-bold mb-4 block" style={{ color: "#b9c3ff" }}>Audience</span>
                  <nav className="flex flex-col gap-1">
                    {[
                      { label: "All Events", value: "All", icon: "groups" },
                      { label: "School", value: "School", icon: "school" },
                      { label: "Founders", value: "Founders", icon: "workspace_premium" },
                      { label: "Parents", value: "Parents", icon: "person" },
                    ].map((item) => (
                      <button
                        key={item.value}
                        onClick={() => setAudienceFilter(item.value)}
                        className={`rounded-lg px-4 py-2.5 flex items-center gap-3 transition-all text-left ${audienceFilter === item.value ? "" : "hover:opacity-80"}`}
                        style={audienceFilter === item.value ? { background: "#26264a", color: "#ffb68f" } : { color: "#c5c5d6" }}
                        data-testid={`filter-audience-${item.value.toLowerCase()}`}
                      >
                        <span className="material-symbols-outlined text-lg">{item.icon}</span>
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>
                {/* Event Type */}
                <div>
                  <span className="text-[10px] uppercase tracking-widest font-bold mb-4 block" style={{ color: "#b9c3ff" }}>Event Type</span>
                  <nav className="flex flex-col gap-1">
                    {[
                      { label: "All Types", value: "All", icon: "event" },
                      { label: "Workshops", value: "Workshop", icon: "architecture" },
                      { label: "Competitions", value: "Competition", icon: "trophy" },
                      { label: "Seminars", value: "Seminar", icon: "school" },
                    ].map((item) => (
                      <button
                        key={item.value}
                        onClick={() => setTypeFilter(item.value)}
                        className={`rounded-lg px-4 py-2.5 flex items-center gap-3 transition-all text-left ${typeFilter === item.value ? "" : "hover:opacity-80"}`}
                        style={typeFilter === item.value ? { background: "#26264a", color: "#ffb68f" } : { color: "#c5c5d6" }}
                        data-testid={`filter-type-${item.value.toLowerCase()}`}
                      >
                        <span className="material-symbols-outlined text-lg">{item.icon}</span>
                        <span>{item.label}</span>
                      </button>
                    ))}
                  </nav>
                </div>
                {/* Mode */}
                <div>
                  <span className="text-[10px] uppercase tracking-widest font-bold mb-4 block" style={{ color: "#b9c3ff" }}>Mode</span>
                  <div className="grid grid-cols-2 gap-2">
                    {["Campus", "Online"].map((m) => (
                      <button
                        key={m}
                        onClick={() => setModeFilter(modeFilter === m ? "All" : m)}
                        className="p-2 rounded-lg text-xs transition-colors"
                        style={modeFilter === m ? { background: "#2f4dc5", color: "#e1dfff", border: "1px solid rgba(185,195,255,0.4)" } : { background: "rgba(49,49,86,0.4)", color: "#e1dfff", border: "1px solid rgba(68,70,84,0.1)" }}
                        data-testid={`filter-mode-${m.toLowerCase()}`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </aside>

            {/* Events Grid */}
            <div className="flex-1">
              {loading ? (
                <div className="flex items-center justify-center py-20" data-testid="events-loading">
                  <div className="w-8 h-8 border-2 border-[#b9c3ff] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8" data-testid="events-grid">
                  {filteredEvents.map((event, index) => (
                    <div
                      key={event.id}
                      className="group rounded-xl overflow-hidden transition-all duration-500 flex flex-col cursor-pointer"
                      style={{ background: "#17173b" }}
                      onClick={() => navigate(`/events/${event.id}`)}
                      data-testid={`event-card-${index}`}
                    >
                      <div className="relative h-56 overflow-hidden">
                        <img
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          src={getCardImage(event, index)}
                          alt={event.title}
                        />
                        <div className="absolute top-4 left-4">
                          <span className={`px-3 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${EVENT_TYPE_COLORS[event.event_type] || "bg-[#313156] text-[#c5c5d6]"}`}>
                            {event.event_type}
                          </span>
                        </div>
                      </div>
                      <div className="p-6 flex-grow flex flex-col">
                        <div className="flex items-center gap-2 text-xs font-semibold mb-3" style={{ color: "#b9c3ff" }}>
                          <span className="material-symbols-outlined text-sm">calendar_today</span>
                          {event.date} {event.time && `\u2022 ${event.time}`}
                        </div>
                        <h3 className="font-bold text-xl text-white mb-3 group-hover:text-[#b9c3ff] transition-colors">
                          {event.title}
                        </h3>
                        <p className="text-sm mb-6 line-clamp-2 leading-relaxed" style={{ color: "#c5c5d6" }}>
                          {event.description}
                        </p>
                        <div className="mt-auto space-y-4">
                          <div className="flex items-center gap-2 text-xs" style={{ color: "rgba(197,197,214,0.7)" }}>
                            <span className="material-symbols-outlined text-sm">{event.mode === "Online" ? "videocam" : "location_on"}</span>
                            {event.location}
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] px-2 py-0.5 rounded" style={{ color: "#8e909f", border: "1px solid rgba(68,70,84,0.3)" }}>
                              {event.audience_tag || event.audience}
                            </span>
                            <span className="text-sm font-bold flex items-center gap-1" style={{ color: "#b9c3ff" }}>
                              View Details
                              <span className="material-symbols-outlined text-lg group-hover:translate-x-1 transition-transform">arrow_right_alt</span>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Suggest Event Card */}
                  <div className="rounded-xl flex flex-col items-center justify-center p-8 text-center group transition-colors" style={{ border: "2px dashed rgba(68,70,84,0.2)" }} data-testid="suggest-event-card">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors" style={{ background: "#26264a" }}>
                      <span className="material-symbols-outlined text-3xl" style={{ color: "#b9c3ff" }}>add_circle</span>
                    </div>
                    <h4 className="font-bold text-lg text-white mb-2">Have a vision?</h4>
                    <p className="text-xs mb-6 max-w-[200px]" style={{ color: "#c5c5d6" }}>
                      Suggest a workshop or community event you'd like to see hosted at Bower.
                    </p>
                    <button className="text-xs font-bold uppercase tracking-widest hover:underline underline-offset-4" style={{ color: "#ffb68f" }}>
                      Get in touch
                    </button>
                  </div>
                </div>
              )}

              {!loading && filteredEvents.length === 0 && (
                <div className="text-center py-20" data-testid="events-empty">
                  <span className="material-symbols-outlined text-5xl mb-4 block" style={{ color: "#444654" }}>event_busy</span>
                  <h3 className="text-xl font-bold text-white mb-2">No events found</h3>
                  <p style={{ color: "#c5c5d6" }}>Try adjusting your filters</p>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="w-full py-16 px-4 md:px-8" style={{ background: "#0a0a28", borderTop: "1px solid rgba(68,70,84,0.15)" }} data-testid="events-footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-10 mb-12">
            {/* Brand */}
            <div className="col-span-2 md:col-span-1">
              <img src={BOWER_LOGO} alt="Bower School" className="h-12 md:h-14 object-contain brightness-0 invert mb-4" />
              <p className="text-sm leading-relaxed" style={{ color: "#8e909f" }}>Building the next generation of industry disruptors.</p>
            </div>

            {/* Programs */}
            <div>
              <h4 className="text-white font-bold text-xs uppercase tracking-widest mb-5">Programs</h4>
              <nav className="flex flex-col gap-3">
                <a href="/beat" className="text-sm hover:text-white transition-colors" style={{ color: "#8e909f" }}>BEAT Test</a>
                <a href="/gitam" className="text-sm hover:text-white transition-colors" style={{ color: "#8e909f" }}>GITAM x Bower BBA</a>
                <a href="/seed" className="text-sm hover:text-white transition-colors" style={{ color: "#8e909f" }}>SEED for Schools</a>
                <a href="https://bowerschool.com/" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-white transition-colors" style={{ color: "#8e909f" }}>3-Year BBA Programme</a>
                <a href="https://bowerschool.com/" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-white transition-colors" style={{ color: "#8e909f" }}>Admissions</a>
              </nav>
            </div>

            {/* Events */}
            <div>
              <h4 className="text-white font-bold text-xs uppercase tracking-widest mb-5">Events</h4>
              <nav className="flex flex-col gap-3">
                <a href="/events" className="text-sm hover:text-white transition-colors" style={{ color: "#8e909f" }}>All Events</a>
                <a href="/admin/events" className="text-sm hover:text-white transition-colors" style={{ color: "#8e909f" }}>Manage Events</a>
              </nav>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-white font-bold text-xs uppercase tracking-widest mb-5">Company</h4>
              <nav className="flex flex-col gap-3">
                <a href="https://bowerschool.com/" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-white transition-colors" style={{ color: "#8e909f" }}>Home</a>
                <a href="https://bowerschool.com/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-white transition-colors" style={{ color: "#8e909f" }}>Terms of Service</a>
                <a href="https://bowerschool.com/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-white transition-colors" style={{ color: "#8e909f" }}>Privacy Policy</a>
                <a href="https://bowerschool.com/refund-policy" target="_blank" rel="noopener noreferrer" className="text-sm hover:text-white transition-colors" style={{ color: "#8e909f" }}>Refund Policy</a>
              </nav>
            </div>
          </div>
          <div className="border-t pt-8 text-center text-sm" style={{ borderColor: "rgba(68,70,84,0.15)", color: "#8e909f" }}>
            &copy; 2025 Bower School of Entrepreneurship. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default EventsListingPage;
